import { SUCCESS } from '@app/constants/general';

export { useHasAllPermissions as useCheckPermissions } from '@app/hooks/use-permission-verify';

const fallbackCopyTextToClipboard = (text) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand('copy');
  } catch {
    // ignore
  }

  document.body.removeChild(textArea);
};

export const copyTextToClipboard = async (text) => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return true;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // ignore
    return false;
  }
};

export const pluralize = (item) =>
  item &&
  `${item.slice(0, item.length - 1)}${item.slice(-1) === 'y' ? 'ies' : `${item.slice(-1)}s`}`;

export const stub = () => {};

export const getTrimmedText = (value) => value?.trim?.() || '';

export const capitalizeEachWord = (text = '') =>
  text
    .trim()
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((val) => val.charAt(0).toUpperCase() + val.substring(1))
    .join(' ');

export const ignoreSpecialCharacters = (text) =>
  text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

/**
 * Modify requiredData with any paths in the redux state which are toggles to blocking the loading of the app.
 * The loading screen will display for ~10 seconds and error out if one of the values are false, preventing rendering
 * of the app.
 * @param {*} state
 */
export const checkInitDone = (state) => {
  const requiredData = [
    'auth.retrievedUserInfo',
    'socket.liveUpdateStrategySelected',
    'profile.retrievedProfileProperties',
  ];
  for (const currPath of requiredData) {
    const itemStatus = currPath
      .split('.')
      .reduce((value, key) => value?.[key], state);
    if (itemStatus !== SUCCESS) {
      return itemStatus;
    }
  }

  return SUCCESS;
};

export const isPasswordlessAuthSession = (authorizedUser) => {
  const passwordlessIdentityProviders = ['FEDERATED'];
  const userIdentityProvider =
    authorizedUser?.user?.ownerOrganization?.identityProvider;
  return passwordlessIdentityProviders.includes(userIdentityProvider);
};

/**
 * Searches currentPermissions array to discover if it contains anything from the substrings array
 * @param {Array} currentPermissions - all current permissions
 * @param {Array} substrings - all valid substrings
 * @returns Boolean of true when any substrings are found in the currentPermissions, false otherwise
 */
export const checkPermissionsContains = (currentPermissions, substrings) => {
  const booleans = substrings.map((substring) =>
    currentPermissions.some((p) => p.includes(substring)),
  );
  return booleans.includes(true);
};

export const hasAnyRole = (userRoles, allowedRoles) =>
  !allowedRoles || userRoles.some((role) => allowedRoles.includes(role));

export const getObjectId = (obj = {}) => obj?.id || obj?._id;

export const isElementVisibleInContainer = (ele, container) => {
  const rect = ele?.getBoundingClientRect();
  const containerRect = container?.getBoundingClientRect();
  return (
    rect.top >= containerRect.top &&
    rect.left >= containerRect.left &&
    rect.bottom <= containerRect.bottom &&
    rect.right <= containerRect.right
  );
};

export const extractTokens = (text) => {
  const tokens = [];
  let body = text;

  // Support both @[$token](token) and @[token](token)
  body = body.replace(
    /@\[(\$?)([^\]]+)\]\(([^)]+)\)/g,
    (_match, _hasDollar, token) => {
      tokens.push({ token });
      return `\${TOKEN}`;
    },
  );

  // Also support plain $token occurrences
  body = body.replace(/\$([A-Za-z][\w]*)/g, (_match, token) => {
    tokens.push({ token });
    return `\${TOKEN}`;
  });

  return { body, tokens };
};

export const isUUID = (id) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export const getOperatorId = (operatorId) =>
  typeof operatorId === 'string' ? operatorId : operatorId?._id;

export const isValidString = (str = '') =>
  typeof str === 'string' &&
  str !== 'null' &&
  str !== 'undefined' &&
  str?.trim() !== '';

export const getIdFromObjectOrString = (objOrString) => {
  if (typeof objOrString === 'string') {
    return objOrString;
  }
  return objOrString?._id || objOrString?.id || '';
};

export const removeLastWhitespace = (text) => {
  return text[text.length - 1] === ' ' ? text.slice(0, -1) : text;
};

export const removeFirstWhitespace = (text) => {
  return text[0] === ' ' ? text.slice(1) : text;
};
